import { useState, useCallback } from 'react';
import api from '../lib/api';
import type { CFMoneyFlowPayload } from '../types/campaignFinance';
import { CF_REQUEST_TIMEOUT_MS } from '../utils/campaignFinanceUtils';

interface UseCFMoneyFlowReturn {
  flow: CFMoneyFlowPayload | null;
  loading: boolean;
  error: string | null;
  load: (coId: string, window?: { start_date?: string; end_date?: string }) => Promise<void>;
  reset: () => void;
}

/**
 * Fetches the self-describing money-flow payload from
 * GET /campaign-finance/committees/:co_id/money-flow — totals, top-5 each side with
 * depth-1 context, badge policy, reconciliation, and methodology, all computed
 * server-side. Windowed by the passed cycle dates; pass none for all-time.
 *
 * A null `flow` after load means the panel falls back to its legacy client-side
 * aggregates (e.g. name-scoped committees that never resolved a co_id).
 */
export function useCFMoneyFlow(): UseCFMoneyFlowReturn {
  const [flow, setFlow] = useState<CFMoneyFlowPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (coId: string, window?: { start_date?: string; end_date?: string }) => {
    if (!coId) return;
    setLoading(true);
    setError(null);
    setFlow(null); // clear the previous committee immediately so it doesn't linger
    try {
      const params = new URLSearchParams();
      if (window?.start_date) params.set('start_date', window.start_date);
      if (window?.end_date) params.set('end_date', window.end_date);
      const qs = params.toString();
      const res = await api.get(
        `/campaign-finance/committees/${encodeURIComponent(coId)}/money-flow${qs ? `?${qs}` : ''}`,
        { timeout: CF_REQUEST_TIMEOUT_MS }
      );
      if (res.data?.success) {
        setFlow(res.data.data as CFMoneyFlowPayload);
      } else {
        setError(res.data?.error || 'Failed to load money flow');
        setFlow(null);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load money flow');
      setFlow(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setFlow(null);
    setError(null);
  }, []);

  return { flow, loading, error, load, reset };
}
